import { Chip, Text } from "react-native-paper";
import React, { useEffect, useState, forwardRef } from 'react';
import { View, Pressable , StyleSheet, Text as NativeText, type TextStyle, Linking, } from "react-native";
import markdownIt from "markdown-it";
//@ts-ignore
import mdSub from "markdown-it-sub";
//@ts-ignore
import mdSup from "markdown-it-sup";
/* -----------------Global--------------- */
import { Platform } from "react-native";
/* -----------------Third parties--------------- */
import Markdown from "react-native-markdown-display";
/* -----------------UI--------------- */
import { type AppTheme, useAppTheme } from "../../useAppTheme";
import { ZoomImage } from "./ZoomImage";
import rules from "./rules";
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { router } from 'expo-router';
import { IconType } from "../TextLoop";


const isWeb = Platform.OS === "web";
const isAndroid = Platform.OS === "android";
const normalizeFontSize = (original: number, custom: number) => {
	const defaultFontSize = 14;
	return (original * custom) / defaultFontSize;
};

export type TextAlign =
	| "left"
	| "auto"
	| "right"
	| "center"
	| "justify"
	| undefined;
const getStyles = (
	theme: AppTheme,
	fontSize = 14,
	textAlign: TextAlign = "left",
	customStyles = {},
	appFont: string = "opensans"
) =>
{
	const styles = StyleSheet.create({
		// Headings
		heading1: {
			fontSize: normalizeFontSize(32, fontSize),
			lineHeight: normalizeFontSize(32, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
			fontWeight: "500",
		},
		heading2: {
			fontSize: normalizeFontSize(24, fontSize),
			lineHeight: normalizeFontSize(24, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
			fontWeight: "500",
		},
		heading3: {
			fontSize: normalizeFontSize(18, fontSize),
			lineHeight: normalizeFontSize(18, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
			fontWeight: "500",
		},
		heading4: {
			fontSize: normalizeFontSize(16, fontSize),
			lineHeight: normalizeFontSize(16, fontSize) * 1.5,
			fontWeight: "500",
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
		},
		heading5: {
			fontSize: normalizeFontSize(13, fontSize),
			lineHeight: normalizeFontSize(13, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
		},
		heading6: {
			fontSize: normalizeFontSize(11, fontSize),
			lineHeight: normalizeFontSize(11, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
		},
		// Horizontal Rule
		hr: {
			backgroundColor: theme.colors.divider,
			marginVertical: 8,
		},
		// Blockquotes
		blockquote: {
			backgroundColor: theme.colors.background,
			borderColor: theme.palette.highlight.main,
			paddingVertical: 8,
			marginLeft: 0,
			paddingLeft: 8,
		},

		// Lists
		bullet_list: {
			fontSize,
			letterSpacing: 0.1,
			lineHeight: fontSize * 1.5,
			marginBottom: fontSize * 0.5,
			color: theme.colors.text,
		},
		ordered_list: {
			fontSize,
			letterSpacing: 0.1,
			lineHeight: fontSize * 1.5,
			color: theme.colors.text,
			fontFamily: `${appFont}-regular`,
		},
		list_item: {
			marginTop: fontSize * 0.2,
			lineHeight: isWeb ? fontSize * 1 : fontSize * 2.2,
			fontSize,
		},
		bullet_list_icon: {
			fontSize: isWeb ? fontSize * 1.5 : isAndroid ? fontSize : 44,
			width: 10,
			marginTop: isAndroid  ? -6 : 0,
			height: fontSize * 1.5,
			alignSelf: 'flex-start',
			marginLeft: 4,
		},
		ordered_list_icon: {
			fontSize: fontSize,
			lineHeight: fontSize * 1.5,
			marginLeft: 4,
		},
		// Code
		code_inline: {
			borderWidth: 1,
			borderColor: theme.colors.divider,
			backgroundColor: theme.colors.surfaceVariant,
			padding: 8,
			lineHeight: Platform.OS === "web" ? fontSize * 2.5 : fontSize * 1.5,
			color: theme.palette.text.primary,
		},
		code_block: {
			borderColor: theme.colors.divider,
			backgroundColor: theme.colors.background,
			color: theme.palette.text.primary,
			fontSize,
		},
		fence: {
			borderColor: theme.colors.divider,
			backgroundColor: theme.colors.background,
			color: theme.palette.text.primary,
			lineHeight: fontSize * 1.3,
			fontSize,
		},

		// Tables
		table: {
			borderColor: theme.colors.divider,
		},
		thead: {
			fontWeight: "bold",
		},
		tr: {
			borderColor: theme.colors.divider,
			padding: 8,
		},
		// Links
		link: {
			textDecorationLine: "underline",
			color: theme.palette.info.dark,
		},
		blocklink: {
			borderColor: theme.colors.divider,
		},
		td: {
			padding: 0,
			paddingHorizontal: 4,
		},
		// Text Output
		text: {
			textAlign,
			color: theme.palette.text.primary,
		},
		textgroup: {
			textAlign,
		},
		paragraph: {},
		// Custom styles
		sup: {
			fontSize: 0.7 * fontSize,
			lineHeight: 0.7 * fontSize * 2,
		},
		subContainer: {
			marginBottom: (-0.7 * fontSize) / 3,
		},
		sub: {
			fontSize: 0.7 * fontSize,
			lineHeight: 0.7 * fontSize,
		},
		image: {},
	});

	for (const key in styles) {
		const style = styles[key as keyof typeof styles];
		const customStyle = customStyles[key as keyof typeof customStyles] as any;
		if (style && customStyle) {
			styles[key as keyof typeof styles] = {
				...style,
				...customStyle,
			};
		}
	}

	return styles;
}

export type CustomStyle = {
	[P in keyof ReturnType<typeof getStyles>]?: any;
};

const InsertableText = ({ content, onInsert, disabled, position }: { 
	content: string; 
	onInsert: (content: string) => void;
	disabled?: boolean;
	position: number;
}) => {
	const theme = useAppTheme();
	
	return (
		<View>
			<Chip
				onPress={content !== "stem" ? () => onInsert(content) : undefined}
				disabled={disabled}
				style={{
					marginBottom: isAndroid ? -4 : -2,
					backgroundColor: theme.palette.highlight.main,
					borderRadius: theme.roundness,
					alignItems: 'center',
					justifyContent: 'center',
					height: 20,
					borderColor: theme.colors.text,
					opacity: disabled ? 0.5 : 1,
				}}
				textStyle={{
					fontSize: 12,
					lineHeight: 16,
					marginVertical: 0,
					marginHorizontal: 4,
					fontFamily: undefined,
					fontWeight: "500",
					color: theme.colors.shadow,
				}}
				mode='outlined'
				compact
				icon={() => (
				<MaterialCommunityIcons 
					name={`numeric-${position}-box-outline` as IconType}
					size={14} 
					color={theme.colors.shadow} 
					style={{ marginTop: isWeb ? 0 : -2 }} 
				/>
			)}
			>
				Insert
			</Chip>
		</View>
	);
};

const HighlightedText = forwardRef<NativeText, { content: string; onPress?: () => void }>(({ content, onPress }, ref) => {
	const theme = useAppTheme();
	
	return (
		<NativeText ref={ref}>
			<Text
				style={{
					color: theme.colors.shadow,
					padding: 2,
					borderRadius: 4,
					backgroundColor: theme.palette.highlight.main,
					fontFamily: undefined,
				}}
				onPress={onPress}
			>
				{content}
			</Text>
		</NativeText>
	);
});

// https://github.com/iamacup/react-native-markdown-display/blob/master/src/lib/renderRules.js
const getRenderRules = (
	fontSize: number,
	textAlign: TextAlign,
	theme: AppTheme,
	customStyles?: CustomStyle,
	useCustomLink?: boolean,
	onInsert?: (content: string) => void,
	handleHighlightedTextPress?: () => void,
	highlightedTextRef?: React.RefObject<NativeText>,
	appFont: string = "opensans",
	disabled?: boolean,
	paragraphMarginVertical: number = 0,
) => {
	const fontFamilyMapping = {
		ultralight: `${appFont}-regular`,
		thin: `${appFont}-regular`,
		light: `${appFont}-regular`,
		normal: `${appFont}-regular`,
		regular: `${appFont}-regular`,
		medium: `${appFont}-semibold`,
		semibold: `${appFont}-semibold`,
		heavy: `${appFont}-semibold`,
		bold: `${appFont}-bold`,
		condensedBold: `${appFont}-bold`,
		condensed: `${appFont}-bold`,
		black: `${appFont}-bold`,
		"100": `${appFont}-regular`,
		"200": `${appFont}-regular`,
		"300": `${appFont}-regular`,
		"400": `${appFont}-regular`,
		"500": `${appFont}-semibold`,
		"600": `${appFont}-bold`,
		"700": `${appFont}-bold`,
		"800": `${appFont}-bold`,
		"900": `${appFont}-bold`,
	};
	return ({

	ins: (_: any, children: any) => (
		<Text
			style={{
				textDecorationLine: "underline",
				fontSize,
			}}
		>
			{children}
		</Text>
	),

	hardbreak: (node: any, _children: any, _: any, styles: any) => (
		<Text key={node.key} style={styles.hardbreak}>
			{"\n"}
		</Text>
	),
	softbreak: (node: any, _children: any, _: any, styles: any) => (
		<Text key={node.key} style={styles.softbreak}>
			{"\n"}
		</Text>
	),

	paragraph: (_node: any, children: React.ReactNode[]) =>
		children.map((item, index) => (
			<NativeText
				key={index}
				style={{
					textAlign,
					fontSize,
					fontWeight: undefined,
					fontFamily: undefined,
					lineHeight: fontSize * 1.5,
					marginVertical: normalizeFontSize(paragraphMarginVertical, fontSize),
					...(customStyles ? customStyles.paragraph : {}),
				}}
			>
				{item}
			</NativeText>
		)),

	strong: (node: any) => {
		const content = node.children[0]?.content || node.children[0]?.children[0]?.content;
		return <Text key={node.key} style={{ fontFamily : `${appFont}-bold`, fontSize, lineHeight: fontSize * 1.5, ...customStyles?.text}}>
			{content}
		</Text>
	},

	em: (node: any, _children: any, _parent: any, _styles: any, inheritedStyles: TextStyle  = {}) => {
		const content = node.children[0]?.content || node.children[0]?.children[0]?.content;
		const markup = node.children[0]?.markup || node.markup;
		const isItalic = markup === '_' || markup === '*';
		return <Text key={node.key} style={{ fontFamily : `${appFont}-${isItalic ? 'regularitalic':'bolditalic'}`,
		 fontSize,
		lineHeight: fontSize * 1.5, ...customStyles?.text}}>
			{content}
		</Text>
	},

	text: (node: any, _children: any, parent: any, _styles: any, inheritedStyles: TextStyle = {}) => {
		const parts = node.content.split(/(\[#.*?\]|\{\{.*?\}\})/);

		return (
			<NativeText
				key={node.key}
				style={{
					color: theme.colors.text,
					...customStyles?.text,
					fontSize: inheritedStyles.fontSize ?? fontSize,
					textAlign,
					flexDirection: 'row',
					flexWrap: 'wrap',
					alignItems: 'center',
					lineHeight: inheritedStyles.fontSize === fontSize ? fontSize * 1.5 : undefined,
					fontFamily: fontFamilyMapping[inheritedStyles.fontWeight ?? customStyles?.text?.fontWeight as keyof typeof fontFamilyMapping ?? '400'],
				}}
			>
				{parts.map((part: string, index: number) => {
					if (part.startsWith('[#') && part.endsWith(']')) {
						const position = part.substring(part.indexOf('+') + 1, part.indexOf(')'))
						
						const content = part.slice(2, -1);
						return (
							<InsertableText
								key={index}
								content={content}
								onInsert={onInsert!}
								disabled={disabled}
								position={parseInt(position)}
							/>
						);
					} else if(part.match(/^\{\{\([^)]+\)[^}]+\}\}$/)) {
						const content = part.replace(/^\{\{\([^)]+\)/, '').slice(0, -2);
						return <HighlightedText key={index} content={content} ref={highlightedTextRef} onPress={handleHighlightedTextPress}/>;
					}
					else if (part.startsWith('{{') && part.endsWith('}}')) {
						const content = part.slice(2, -2);
						return <HighlightedText key={index} content={content} ref={highlightedTextRef} onPress={handleHighlightedTextPress}/>;
					}
					return part;
				})}
			</NativeText>
		);
	},

	...(useCustomLink
		? {
				link: (node: any, children: any, _: any, styles: any) => (
						<Text key={node.key} onPress={() => {
							const href = node.attributes.href;
							const fragment = href.split("#")[1];
							if (href.startsWith("http")) {
								Linking.openURL(href)
							} else {
								router.push(href as any);
							}
							if (fragment) {
								setTimeout(() => {
									window.location.hash = `${fragment}`;
								}, 500);
							}
						}} style={{ textDecorationLine: "underline", color: theme.palette.info.dark }}>{children}</Text>
				),
			}
		: {}),

	sup: (_node: any, children: any, _: any, styles: any) => (
		<View>
			<Text style={styles.sup}>{children}</Text>
		</View>
	),

	sub: (_node: any, children: any, _: any, styles: any) => (
		<View style={styles.subContainer}>
			<Text style={styles.sub}>{children}</Text>
		</View>
	),
	image: (node: any) => {
		const { src } = node.attributes;

		return (
			<ZoomImage
				key={node.key}
				url={src}
				allImages={[
					{
						url: src,
					},
				]}
				{...customStyles?.image}
			/>
		);
	},

	
})
};

/* -----------------Props--------------- */
export type MarkdownRendererProps = {
	text?: string;
	fontSize?: number;
	textAlign?: TextAlign;
	variant?: "term" | "fact" | "description" | "stem" | "answer" | "explanation";
	customStyles?: CustomStyle;
	useCustomLink?: boolean;
	overrideRules?: Record<string, any>;
	handleClickInsertableText?: (choiceId: string) => void;
	handleHighlightedTextPress?: () => void;
	highlightedTextRef?: React.RefObject<NativeText>;
	appFont?: string;
	disableInsertableText?: boolean;
	paragraphMarginVertical?: number;
};

/* -----------------Component--------------- */
const RenderMarkdown = (props: MarkdownRendererProps) => {
	const {
		text,
		fontSize = 14,
		textAlign = "left",
		variant = "explanation",
		customStyles,
		useCustomLink = true,
		overrideRules,
		handleClickInsertableText,
		handleHighlightedTextPress,
		highlightedTextRef,
		appFont,
		disableInsertableText = false,
		paragraphMarginVertical,
	} = props;

	const [markdownContent, setMarkdownContent] = useState(text || '');

	const theme = useAppTheme();
	const styles = getStyles(theme, fontSize, textAlign, customStyles, appFont);

	const handleInsert = (content: string) => {
		const choiceId = content.slice(1, content.indexOf('+'));
		handleClickInsertableText?.(choiceId);
	};

	const finalStyles = {
		...styles,
	};
	const { config, disabled } = rules[variant];
	const md = markdownIt(config);
	md.use(mdSup).use(mdSub);
	md.disable(disabled);

	const renderRules = getRenderRules(
		fontSize,
		textAlign,
		theme,
		customStyles,
		useCustomLink,
		handleInsert,
		handleHighlightedTextPress,
		highlightedTextRef,
		appFont,
		disableInsertableText,
		paragraphMarginVertical
	);

	useEffect(() => {
		setMarkdownContent(text  ?? '');
	}, [text]);

	return (
			<Markdown
				style={finalStyles}
				markdownit={md}
				rules={{
					...renderRules,
					...overrideRules,
				}}
			>
				{markdownContent}
			</Markdown>
	);
};

export default RenderMarkdown;
